import React, { useState, useEffect, useRef } from 'react';
import Axios from "axios";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc'; 
import RequestDocModal from '../AdditionalDocModal/AdditionalDocModal'; // Now works because we fixed the export


// UI Components
import { 
  FileText, 
  ChevronDown, 
  Eye, 
  MoreVertical, 
  Download, 
  Trash2, 
  MessageSquarePlus, 
  CheckCircle2, 
  PlusCircle, 
  AlertCircle,
  FileQuestion
} from 'lucide-react';

// Keep MUI Dialogs
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

dayjs.extend(utc); 

const TableDoc = (props) => {
  const backEndUrl = process.env.REACT_APP_SERVER_ADDRESS;

  // --- STATE MANAGEMENT ---
  const [consultantData, setConsultantData] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [openMenuRowId, setOpenMenuRowId] = useState(null);
  const menuRef = useRef(null);

  // Dialog States
  const [openDelete, setOpenDelete] = useState(false);
  const [openCorrect, setOpenCorrect] = useState(false);
  const [openDemoModal, setOpenDemoModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  
  // 1. ADDED: State for Request New Doc Modal
  const [openRequestModal, setOpenRequestModal] = useState(false); 

  // Form States (Correction)
  const [docName, setDocName] = useState('');
  const [docDescription, setDocDescription] = useState('');
  const [dateDeadline, setdateDeadline] = useState(dayjs.utc(props.deadline));
  const [warningDate, setWarningDate] = useState(false);

  // ... [Keep your existing useEffects and Helper functions exactly the same] ...

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuRowId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    Axios({
      method: "GET",
      withCredentials: true,
      url: `${backEndUrl}/auth/consultantLog`,
    }).then((res) => {
      setConsultantData(res.data);       
    });
  }, [backEndUrl]);

  const handleCreateDoc = async (formDataOrArray) => {
    // Convert to array to handle batch requests
    const requests = Array.isArray(formDataOrArray) ? formDataOrArray : [formDataOrArray];
    
    try {
      const promises = requests.map(req => {
        const deadline = req.deadline && req.deadline.trim() !== '' 
          ? dayjs.utc(req.deadline) 
          : dateDeadline;
          
        return Axios.post(`${backEndUrl}/updateClient/addDoc`, {
          doc_name: req.name,
          doc_description: req.description,
          client_id: props.ridi,
          deadline: deadline,
        }, {
          withCredentials: true
        });
      });

      await Promise.all(promises);
      // Success - reload to show new doc
      window.location.reload();
    } catch (error) {
      console.error("Error creating document:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to create document request.";
      alert(errorMessage);
      // Re-throw error so modal can handle it
      throw error;
    }
  };

  const getLocalizedValue = (rawField) => {
    const userLang = consultantData?.preferredLanguage || 'english'; 
    if (!rawField) return '';
    if (typeof rawField === 'string') return rawField;
    if (typeof rawField === 'object') {
        return userLang === 'french' ? (rawField.fr || rawField.en || '') : (rawField.en || '');
    }
    return '';
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // ... [Keep your existing Action Handlers (Review, View, Download, Delete, Correct) exactly the same] ...

  const handleReviewClick = async (doc, e) => {
    e.stopPropagation();
    try {
      await Axios({
          method: "PUT",
          withCredentials: true,
          data: {
            client_id: props.ridi,
            document_id: doc._id,
            review: doc.review, 
          },
          url : `${backEndUrl}/updateClient/setReview/` 
      })
      window.location.reload();
    } catch (error) {
      console.error("Error updating review status", error);
    }
  };

  const handleView = async (doc) => {
    if (!doc.doc_upload) {
      if (props.isDemo) setOpenDemoModal(true);
      return;
    }
    try {
      const localizedName = getLocalizedValue(doc.name);
      const localizedDescription = getLocalizedValue(doc.description);
      const response = await fetch(`${backEndUrl}/updateClient/viewDoc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ key: doc.doc_upload }), 
      });
      const data = await response.json();
      const queryParams = new URLSearchParams({
        url: data,
        docName: localizedName,
        docDescription: localizedDescription,
        userId: props.ridi, 
        docID : doc._id,
        deadline: props.deadline
      }).toString();
      const viewDocumentUrl = `/view-document?${queryParams}`;
      window.open(viewDocumentUrl, '_blank');
    } catch (error) {
      console.error('Error fetching document:', error);
    }
  };

  const handleDownload = (key) => {
    if (!key) return;
    window.location.href = `${backEndUrl}/updateClient/download/${key}`;
  };

  const openDeleteDialog = (doc) => {
    setSelectedDoc(doc);
    setOpenDelete(true);
    setOpenMenuRowId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedDoc) return;
    try {
      await Axios({
        method: 'DELETE',
        withCredentials: true,
        data: {
            doc_id: selectedDoc._id,
            client_id: props.ridi,
            storageUsedByConsultant: props.storageUsed,
            docSize: selectedDoc.size,
            path: selectedDoc.doc_upload,
        },
        url: `${backEndUrl}/updateClient/deleteSpecificDocument`,
      });
      window.location.reload();
    } catch (error) {
      console.error("Delete failed", error);
    }
  };

  const openCorrectionDialog = (doc) => {
    const localizedName = getLocalizedValue(doc.name);
    const localizedDescription = getLocalizedValue(doc.description);
    const currentDate = dayjs.utc();
    if (currentDate.isAfter(dayjs.utc(props.deadline))) {
      setWarningDate(true);
    } else {
      setWarningDate(false);
    }
    setDocName(localizedName);
    setDocDescription(localizedDescription);
    setSelectedDoc(doc);
    setOpenCorrect(true);
    setOpenMenuRowId(null);
  };

  const handleSendCorrection = () => {
    Axios.post(`${backEndUrl}/updateClient/addDoc`, {
      doc_name: docName,
      doc_description: docDescription,
      client_id: props.ridi,
      deadline: dateDeadline,
    }, {
      withCredentials: true
    })
    .then(response => {
      window.location.reload();
    })
    .catch(error => {
      console.error("Error:", error);
    });
  };

  const handleDateChange = (e) => {
    const newDate = dayjs.utc(e.target.value);
    setdateDeadline(newDate);
    const currentDate = dayjs.utc();
    if (currentDate.isAfter(newDate)) {
      setWarningDate(true);
    } else {
      setWarningDate(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border-2 border-slate-100 w-full overflow-hidden mt-6 font-sans backdrop-blur-sm">
      
      {/* HEADER TABS - Removed in favor of page-level tabs, replaced with simple header */}
      <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
         <h3 className="font-bold text-sm text-slate-700 uppercase tracking-wider">Required Documents</h3>
         
         {/* 2. UPDATED: Button now toggles the modal state */}
         <button 
           onClick={() => setOpenRequestModal(true)} 
           className="text-brand-600 text-sm font-bold hover:underline flex items-center gap-1 text-blue-600"
         >
           <PlusCircle className="h-4 w-4" /> Request New Doc
         </button>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto min-h-[300px] bg-gradient-to-b from-white to-slate-50/30">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="text-slate-500 text-xs font-semibold uppercase tracking-wider border-b border-slate-200/60 bg-slate-50/80 backdrop-blur-sm">
              <th className="px-8 py-5 pl-8 w-1/3">Document & Requirements</th>
              <th className="px-6 py-5">Status</th>
              <th className="px-6 py-5">Date</th>
              <th className="px-6 py-5 text-right pr-8">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {props.clients && props.clients.map((doc) => {
              const isExpanded = expandedRows[doc._id];
              const localizedName = getLocalizedValue(doc.name);
              const localizedDescription = getLocalizedValue(doc.description);
              const isUploaded = doc.status !== 'not uploaded';
              const isReviewed = doc.review === 'good';
              
              return (
                <tr 
                  key={doc._id} 
                  className={`group transition-all duration-300 hover:bg-blue-50/50 hover:shadow-sm ${isExpanded ? 'bg-slate-50' : ''}`}
                >
                  <td className="px-8 py-5 pl-8 align-top border-l-4 border-l-transparent group-hover:border-l-blue-600 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="mt-1 p-2 bg-slate-100 rounded-lg text-slate-500">
                         {isUploaded ? <FileText className="h-5 w-5 text-blue-600" /> : <FileQuestion className="h-5 w-5" />}
                      </div>
                      <div className="w-full">
                        <div 
                          className="font-bold text-slate-800 flex items-center gap-2 cursor-pointer hover:text-blue-600 transition-colors"
                          onClick={() => toggleRow(doc._id)}
                        >
                          {localizedName}
                          <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                        <div className={`relative overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-96 opacity-100 mt-2' : 'max-h-6 opacity-60'}`}>
                            <p className={`text-xs text-slate-500 leading-relaxed ${!isExpanded ? 'line-clamp-1' : ''}`} onClick={() => toggleRow(doc._id)}>
                              {localizedDescription || "No requirements specified."}
                            </p>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5 align-top pt-6">
                    {isUploaded ? (
                      <div className="flex flex-col gap-2">
                         <button 
                           onClick={(e) => handleReviewClick(doc, e)}
                           className={`group relative inline-flex items-center justify-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200 border-2 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-1 w-36 ${
                             isReviewed 
                               ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 focus:ring-emerald-500' 
                               : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300 focus:ring-blue-500'
                           }`}
                           title={isReviewed ? "Click to mark as Ready to Review" : "Click to mark as Approved"}
                         >
                           {isReviewed ? (
                             <>
                               <CheckCircle2 className="h-3.5 w-3.5" /> 
                               <span>Approved</span>
                             </>
                           ) : (
                             <>
                               <span className="relative flex h-2 w-2">
                                 <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                                 <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                               </span>
                               <span>Ready to Review</span>
                             </>
                           )}
                         </button>
                      </div>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
                        Waiting for client
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-5 align-top pt-6">
                    <span className="text-sm font-medium text-slate-600">
                      {doc.date ? new Date(doc.date).toLocaleDateString() : "-"}
                    </span>
                  </td>
                  <td className="px-6 py-5 pr-8 align-top pt-6 text-right relative">
                    <div className="flex items-center justify-end gap-3">
                      {isUploaded && (
                        <button onClick={() => handleView(doc)} className="flex items-center gap-1.5 bg-white border border-slate-200 hover:border-blue-300 hover:text-blue-600 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all">
                          <Eye className="h-3 w-3" /> View
                        </button>
                      )}
                      <button 
                        onClick={(e) => { e.stopPropagation(); setOpenMenuRowId(openMenuRowId === doc._id ? null : doc._id); }}
                        className={`p-1.5 rounded-lg transition-colors ${openMenuRowId === doc._id ? 'bg-slate-100 text-slate-900' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                      {openMenuRowId === doc._id && (
                        <div ref={menuRef} className="absolute right-8 top-14 w-48 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden text-left animate-in fade-in zoom-in-95 duration-200 ring-1 ring-slate-900/5">
                          {isUploaded && (
                            <button onClick={() => handleDownload(doc.doc_upload)} className="w-full text-left px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors">
                              <Download className="h-4 w-4 text-slate-400" /> Download
                            </button>
                          )}
                          <button onClick={() => openCorrectionDialog(doc)} className="w-full text-left px-4 py-3 text-sm font-medium text-slate-700 hover:bg-orange-50 hover:text-orange-700 flex items-center gap-3 transition-colors">
                            <MessageSquarePlus className="h-4 w-4 text-slate-400" /> Request Correction
                          </button>
                          <div className="h-px bg-slate-100 my-1"></div>
                          <button onClick={() => openDeleteDialog(doc)} className="w-full text-left px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors">
                            <Trash2 className="h-4 w-4" /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {(!props.clients || props.clients.length === 0) && (
              <tr><td colSpan="4" className="px-8 py-12 text-center text-slate-500">No documents found for this client.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* --- DIALOGS --- */}

      {/* DEMO MODE DIALOG */}
      {(() => {
        const isFrench = consultantData?.preferredLanguage === 'french';
        return (
          <Dialog open={openDemoModal} onClose={() => setOpenDemoModal(false)} PaperProps={{ sx: { borderRadius: '20px', px: 1, py: 0.5, minWidth: 380, maxWidth: 440 } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', pb: 0.5 }}>
              {isFrench ? '🔒 Mode démo' : '🔒 Demo mode'}
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: '#475569', lineHeight: 1.8, fontSize: '0.95rem' }}>
                {isFrench ? (
                  <>Ceci est une demo.<br />Le document réel de votre client s'affichera ici en haute résolution.</>
                ) : (
                  <>This is a demo.<br />Your client's actual document will appear here in full resolution.</>
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button onClick={() => setOpenDemoModal(false)} variant="contained" sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, background: '#0ea5e9', '&:hover': { background: '#0284c7' } }}>
                {isFrench ? 'Compris' : 'Got it'}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* DELETE DIALOG */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle className="font-bold text-slate-900">Delete this document?</DialogTitle>
        <DialogContent>
          <DialogContentText className="text-slate-600">
            Are you sure you want to delete <span className="font-semibold text-slate-800">{selectedDoc && getLocalizedValue(selectedDoc.name)}</span>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions className="p-4">
          <Button onClick={() => setOpenDelete(false)} variant="outlined" color="inherit">Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" autoFocus>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* CORRECTION DIALOG */}
      <Dialog open={openCorrect} onClose={() => setOpenCorrect(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="font-bold text-slate-900 flex items-center gap-2">
          <MessageSquarePlus className="h-5 w-5 text-orange-500" /> Request Correction
        </DialogTitle>
        <DialogContent>
          <DialogContentText className="mb-4 text-slate-600 text-sm">
            Use this form to update requirements or request changes. Don't forget to press Submit to notify the client.
          </DialogContentText>
          <TextField margin="dense" label="Document Name" fullWidth variant="outlined" value={docName} onChange={(e) => setDocName(e.target.value)} className="mb-4" />
          <TextField margin="dense" label="Requirements / Instructions" fullWidth multiline rows={4} variant="outlined" value={docDescription} onChange={(e) => setDocDescription(e.target.value)} />
          <div className="mt-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">New Deadline</label>
            <input type="date" className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" value={dateDeadline ? dateDeadline.format('YYYY-MM-DD') : ''} onChange={handleDateChange} />
            {warningDate && <p className="flex items-center gap-2 text-red-600 text-xs mt-2 font-medium"><AlertCircle className="h-3 w-3" /> Warning: The selected deadline is in the past.</p>}
          </div>
        </DialogContent>
        <DialogActions className="p-4 border-t border-slate-100">
          <Button onClick={() => setOpenCorrect(false)} variant="outlined" color="inherit">Cancel</Button>
          <Button onClick={handleSendCorrection} variant="contained" className="bg-blue-600 hover:bg-blue-700">Submit & Notify</Button>
        </DialogActions>
      </Dialog>

      {/* 3. ADDED: Request New Doc Modal Rendered Here */}
      <RequestDocModal 
        isOpen={openRequestModal} // Changed from 'open' to 'isOpen'
        onClose={() => setOpenRequestModal(false)} // Changed from 'setOpen' to 'onClose'
        onSubmit={handleCreateDoc} // Pass the submit handler
        clientDeadline={props.deadline}
        clientPreferredLanguage={consultantData?.preferredLanguage === 'french' ? 'fr' : 'en'}
        consultantTemplates={consultantData?.docTemplates || []}
      />

    </div>
  );
};

export default TableDoc;